import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import {
  Save,
  ExternalLink,
  ChevronLeft,
  Upload,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Search,
  Tag as TagIcon,
  Globe,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const PUBLIC_ORIGIN = import.meta.env.VITE_PUBLIC_ORIGIN || 'https://hitzlink.com';

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content_md: string;
  content_html: string | null;
  cover_image_url: string | null;
  category: string | null;
  tags: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  canonical_url: string | null;
  og_image_url: string | null;
  author_id: string | null;
  author_name_override: string | null;
  reading_time_minutes: number | null;
  word_count: number | null;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
  scheduled_at: string | null;
  noindex: boolean;
  locale: string;
  view_count: number;
  created_at: string;
  updated_at: string;
}

// LocalStorage key namespace so unsaved drafts survive a tab refresh
// even before the server-side autosave fires. We bind by article id
// so concurrent tabs on different articles don't trample each other.
function draftKey(id: string) {
  return `hitzlink:article-draft:${id}`;
}

const COUNTER_LIMITS = { title: 60, description: 160 } as const;

function counterColor(value: string, max: number): string {
  const len = value?.length || 0;
  if (len === 0) return 'text-gray-400';
  if (len > max) return 'text-red-600';
  if (len > max - 10) return 'text-amber-600';
  return 'text-gray-500';
}

export const ArticleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [seoOpen, setSeoOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const authHeader = useMemo<Record<string, string>>(
    () => (token ? { Authorization: `Bearer ${token}` } : ({} as Record<string, string>)),
    [token]
  );

  // Fetch
  useEffect(() => {
    let cancelled = false;
    if (!id || !token) return;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/admin/articles/${id}`, { headers: authHeader });
        if (!res.ok) throw new Error('Failed to load');
        const data = await res.json();
        if (cancelled) return;

        // If we have an unsaved local draft newer than the server
        // `updated_at`, prefer the local one. Useful when the user
        // closed the tab mid-edit.
        try {
          const raw = localStorage.getItem(draftKey(data.id));
          if (raw) {
            const draft = JSON.parse(raw);
            if (draft && draft.savedAt && new Date(draft.savedAt) > new Date(data.updated_at)) {
              data.content_md = draft.content_md ?? data.content_md;
              data.title = draft.title ?? data.title;
              data.excerpt = draft.excerpt ?? data.excerpt;
              toast('Restored unsaved local draft', { icon: '🔄' });
            }
          }
        } catch { /* swallow */ }

        setArticle(data);
        setLastSavedAt(new Date(data.updated_at));
      } catch (err) {
        console.error(err);
        toast.error('Failed to load article');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, token, authHeader]);

  const patch = useCallback(<K extends keyof Article>(key: K, value: Article[K]) => {
    setArticle((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  }, []);

  // Persist unsaved changes locally on every change (debounced) so a
  // browser crash doesn't lose work between server autosaves.
  useEffect(() => {
    if (!article) return undefined;
    const handle = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey(article.id),
          JSON.stringify({
            content_md: article.content_md,
            title: article.title,
            excerpt: article.excerpt,
            savedAt: new Date().toISOString(),
          })
        );
      } catch { /* quota — ignore */ }
    }, 500);
    return () => clearTimeout(handle);
  }, [article?.content_md, article?.title, article?.excerpt, article?.id]);

  // Warn before close while dirty.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const save = useCallback(async (opts?: { silent?: boolean }) => {
    if (!article) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/articles/${article.id}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: article.slug,
          title: article.title,
          excerpt: article.excerpt,
          content_md: article.content_md,
          cover_image_url: article.cover_image_url,
          category: article.category,
          tags: article.tags,
          seo_title: article.seo_title,
          seo_description: article.seo_description,
          seo_keywords: article.seo_keywords,
          canonical_url: article.canonical_url,
          og_image_url: article.og_image_url,
          author_name_override: article.author_name_override,
          scheduled_at: article.scheduled_at,
          noindex: article.noindex,
          locale: article.locale,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      const updated = await res.json();
      setArticle(updated);
      setLastSavedAt(new Date());
      setDirty(false);
      // Clear the local draft once we've successfully synced it.
      try { localStorage.removeItem(draftKey(updated.id)); } catch { /* */ }
      if (!opts?.silent) toast.success('Saved');
    } catch (err: any) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [article, authHeader]);

  // Auto-save every 30 seconds while dirty. Skips when offline or
  // already saving to avoid backed-up requests.
  useEffect(() => {
    if (!dirty || !article) return undefined;
    const handle = setTimeout(() => {
      if (!saving) save({ silent: true });
    }, 30_000);
    return () => clearTimeout(handle);
  }, [dirty, article, saving, save]);

  // Cmd/Ctrl+S keyboard shortcut.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [save]);

  const togglePublish = async () => {
    if (!article) return;
    if (dirty) {
      await save({ silent: true });
    }
    const action = article.status === 'published' ? 'unpublish' : 'publish';
    try {
      const res = await fetch(`${API_URL}/admin/articles/${article.id}/${action}`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setArticle(updated);
      toast.success(action === 'publish' ? 'Published' : 'Moved to draft');
    } catch {
      toast.error('Action failed');
    }
  };

  const uploadImage = async (file: File, target: 'cover' | 'inline'): Promise<string | null> => {
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${API_URL}/admin/articles/upload-image`, {
        method: 'POST',
        headers: authHeader,
        body: fd,
      });
      if (!res.ok) throw new Error('Upload failed');
      const json = await res.json();
      if (!json.url) throw new Error('No URL returned');
      if (target === 'cover') {
        patch('cover_image_url', json.url);
      }
      return json.url as string;
    } catch (err) {
      toast.error('Image upload failed');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const insertImageMarkdown = async (file: File) => {
    const url = await uploadImage(file, 'inline');
    if (!url || !article) return;
    const insertion = `\n\n![${file.name.replace(/\.[^.]+$/, '')}](${url})\n\n`;
    patch('content_md', (article.content_md || '') + insertion);
  };

  if (loading || !article) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col h-full">
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/admin/articles')}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="w-4 h-4" />
              Articles
            </button>
            <span className="text-gray-300">/</span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{article.title || 'Untitled article'}</p>
              <p className="text-xs text-gray-500 font-mono truncate">/blog/{article.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 flex items-center gap-1 mr-2">
              {saving ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
              ) : dirty ? (
                <><AlertCircle className="w-3 h-3 text-amber-500" /> Unsaved changes</>
              ) : lastSavedAt ? (
                <><CheckCircle2 className="w-3 h-3 text-green-500" /> Saved {lastSavedAt.toLocaleTimeString()}</>
              ) : null}
            </span>
            {article.status === 'published' && (
              <a
                href={`${PUBLIC_ORIGIN}/blog/${article.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View public
              </a>
            )}
            <button
              onClick={() => save()}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              Save draft
            </button>
            <button
              onClick={togglePublish}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${
                article.status === 'published'
                  ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-200'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {article.status === 'published' ? 'Unpublish' : 'Publish'}
            </button>
          </div>
        </div>

        {/* Body — two-column on desktop, stacked on mobile */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            {/* Main editor column */}
            <div className="space-y-4">
              {/* Title + slug + excerpt */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={article.title}
                    onChange={(e) => patch('title', e.target.value)}
                    className="w-full px-3 py-2 text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Headline that converts..."
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">URL slug</label>
                    <input
                      type="text"
                      value={article.slug}
                      onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      className="w-full px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Locale</label>
                    <select
                      value={article.locale}
                      onChange={(e) => patch('locale', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="th">Thai</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Excerpt</label>
                  <textarea
                    value={article.excerpt || ''}
                    onChange={(e) => patch('excerpt', e.target.value)}
                    rows={2}
                    placeholder="Short summary shown in /blog list and OG previews..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>

              {/* Markdown editor with toolbar */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-color-mode="light">
                <MDEditor
                  value={article.content_md || ''}
                  onChange={(v) => patch('content_md', v || '')}
                  height={640}
                  preview="live"
                  visibleDragbar={false}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
                  <div className="flex items-center gap-3">
                    <span>{article.word_count ?? 0} words</span>
                    <span>·</span>
                    <span>{article.reading_time_minutes ?? 0} min read</span>
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                    Upload image to article
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) insertImageMarkdown(file);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  />
                </div>
              </div>

              {/* SEO panel — collapsed by default */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSeoOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-gray-900">SEO &amp; Social</span>
                    <span className="text-xs text-gray-500">Override defaults from System Settings</span>
                  </div>
                  {seoOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {seoOpen && (
                  <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700">SEO title</label>
                        <span className={`text-xs ${counterColor(article.seo_title || '', COUNTER_LIMITS.title)}`}>
                          {(article.seo_title || '').length} / {COUNTER_LIMITS.title}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={article.seo_title || ''}
                        onChange={(e) => patch('seo_title', e.target.value)}
                        placeholder={article.title || 'Falls back to article title'}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700">SEO description</label>
                        <span className={`text-xs ${counterColor(article.seo_description || '', COUNTER_LIMITS.description)}`}>
                          {(article.seo_description || '').length} / {COUNTER_LIMITS.description}
                        </span>
                      </div>
                      <textarea
                        value={article.seo_description || ''}
                        onChange={(e) => patch('seo_description', e.target.value)}
                        rows={2}
                        placeholder={article.excerpt || 'Falls back to excerpt'}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Canonical URL (optional)</label>
                      <input
                        type="url"
                        value={article.canonical_url || ''}
                        onChange={(e) => patch('canonical_url', e.target.value)}
                        placeholder={`${PUBLIC_ORIGIN}/blog/${article.slug}`}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Open Graph image URL</label>
                      <input
                        type="url"
                        value={article.og_image_url || ''}
                        onChange={(e) => patch('og_image_url', e.target.value)}
                        placeholder="Falls back to cover image or platform default"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Keywords (comma-separated)</label>
                      <input
                        type="text"
                        value={article.seo_keywords || ''}
                        onChange={(e) => patch('seo_keywords', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={article.noindex}
                        onChange={(e) => patch('noindex', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        <strong>noindex</strong> — hide from search engines (good for internal posts, announcements)
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-4">
              {/* Cover image */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-blue-600" />
                  Cover image
                </h3>
                {article.cover_image_url ? (
                  <div className="space-y-2">
                    <div className="aspect-[16/9] rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                      <img src={article.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => coverInputRef.current?.click()}
                        className="flex-1 text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Replace
                      </button>
                      <button
                        onClick={() => patch('cover_image_url', null)}
                        className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 border border-red-200 rounded-md"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => coverInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg py-6 hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-gray-600 flex flex-col items-center gap-2 disabled:opacity-50"
                  >
                    {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                    Upload cover image
                  </button>
                )}
                <input
                  type="file"
                  ref={coverInputRef}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file, 'cover');
                    if (coverInputRef.current) coverInputRef.current.value = '';
                  }}
                />
              </div>

              {/* Taxonomy */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <TagIcon className="w-4 h-4 text-blue-600" />
                  Taxonomy
                </h3>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={article.category || ''}
                    onChange={(e) => patch('category', e.target.value)}
                    placeholder="Tutorial, Case study, Guide..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={(article.tags || []).join(', ')}
                    onChange={(e) => patch('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))}
                    placeholder="affiliate, lead-gen, ..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Author + publish */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  Publishing
                </h3>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Author (override)</label>
                  <input
                    type="text"
                    value={article.author_name_override || ''}
                    onChange={(e) => patch('author_name_override', e.target.value)}
                    placeholder="Leave blank to use default"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-100">
                  <div className="flex justify-between"><span>Status:</span><span className="font-medium text-gray-700">{article.status}</span></div>
                  <div className="flex justify-between"><span>Published:</span><span>{article.published_at ? new Date(article.published_at).toLocaleDateString() : '—'}</span></div>
                  <div className="flex justify-between"><span>Views:</span><span>{article.view_count.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span>Updated:</span><span>{new Date(article.updated_at).toLocaleString()}</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ArticleEditor;
